import { FormEvent, useEffect, useState } from 'react';
import PriceInput from '../../../components/inputs/Price';
import { useAppContext } from '../../../contexts/AppContext';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { SettingsRow, SettingsSection } from '../../configuracoes/estabelecimento/Form/SettingsGroup';
import { useEstoqueContext } from '../Context';
import { EstoqueService } from '../Service';
import { MOVEMENT_TYPE_LABELS, MOVEMENT_WARNING_LABELS, formatQuantity } from '../units';
import { PackageOpen, TriangleAlert } from 'lucide-react';

const service = new EstoqueService();

const emptyForm = {
    itemId: '',
    type: 'ENTRADA',
    quantity: 1,
    reasonCode: '',
    adjustmentDirection: '',
    unitCost: '' as string | number,
    notes: '',
};

export default function EstoqueMovement() {
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { id, setParams } = useParamsContext();
    const { reloadSummary, reloadRecommendations } = useEstoqueContext();
    const [items, setItems] = useState<any[]>([]);
    const [loadingItems, setLoadingItems] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [form, setForm] = useState<any>(emptyForm);

    const selected = items.find((item) => item._id === form.itemId);

    const selectItem = (itemId: string, list: any[] = items) => {
        const item = list.find((current) => current._id === itemId);
        setForm((prev: any) => ({ ...prev, itemId, unitCost: item?.costPrice ?? '' }));
    };

    const loadItems = async () => {
        setLoadingItems(true);
        setLoadError(false);
        try {
            const result = await service.findItems({ isActive: true, limit: 200, page: 1 }).then((r) => r?.data || {});
            const list = result?.data || [];
            setItems(list);
            return list;
        } catch (error) {
            console.error(error);
            setLoadError(true);
            return [];
        } finally {
            setLoadingItems(false);
        }
    };

    useEffect(() => {
        // Vindo do detalhe, o item ja chega selecionado.
        void loadItems().then((list) => {
            if (id) selectItem(id, list);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (field: string, value: any) => {
        setForm((prev: any) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        if (!(Number(form.quantity) > 0)) {
            errorSnackbar('Informe uma quantidade maior que zero.');
            return;
        }

        if (form.type === 'AJUSTE' && !form.adjustmentDirection) {
            errorSnackbar('Informe se o ajuste adiciona ou retira do saldo.');
            return;
        }

        setSaving(true);
        setWarnings([]);
        try {
            const result = await service
                .createMovement({
                    itemId: form.itemId,
                    type: form.type,
                    quantity: Number(form.quantity),
                    reasonCode: form.reasonCode || undefined,
                    adjustmentDirection: form.type === 'AJUSTE' ? form.adjustmentDirection : undefined,
                    unitCost: form.unitCost !== '' ? Number(form.unitCost) : undefined,
                    notes: form.notes || undefined,
                })
                .then((r) => r?.data || {});
            const nextWarnings: string[] = result?.warnings || [];

            successSnackbar('Movimentação registrada');
            void reloadSummary();
            void reloadRecommendations();

            if (id && !nextWarnings.length) {
                setParams({ action: 'detail', id });
                return;
            }

            setWarnings(nextWarnings);
            setForm(emptyForm);
            await loadItems();
        } catch (error: any) {
            console.error(error);
            errorSnackbar(error?.response?.data?.message || 'Erro ao registrar movimentação');
        } finally {
            setSaving(false);
        }
    };

    if (loadError) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar os itens</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void loadItems()}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!loadingItems && !items.length) {
        return (
            <div className="sf-state">
                <PackageOpen aria-hidden />
                <h2>Nenhum item ativo</h2>
                <p>Cadastre um item para registrar movimentações.</p>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setParams({ action: 'items', id: '' })}>
                    Ver itens
                </button>
            </div>
        );
    }

    return (
        <form className="estab-settings mx-auto w-full max-w-2xl space-y-6" onSubmit={handleSubmit}>
            <div className="sf-segmented" role="group" aria-label="Tipo de movimentação">
                {Object.entries(MOVEMENT_TYPE_LABELS).map(([value, label]) => (
                    <button key={value} type="button" aria-pressed={form.type === value} onClick={() => handleChange('type', value)}>
                        {label}
                    </button>
                ))}
            </div>

            <SettingsSection
                footer={selected ? `Saldo atual: ${formatQuantity(selected.balance?.onHand)} ${selected.unit}` : undefined}
            >
                <SettingsRow label="Item" htmlFor="mov-item">
                    <select
                        id="mov-item"
                        className="select estab-field"
                        value={form.itemId}
                        onChange={(e) => selectItem(e.target.value)}
                        disabled={loadingItems}
                        required
                    >
                        <option value="">Selecione</option>
                        {items.map((item) => (
                            <option key={item._id} value={item._id}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                </SettingsRow>
                <SettingsRow label={selected?.unit ? `Quantidade (${selected.unit})` : 'Quantidade'} htmlFor="mov-quantidade">
                    <input
                        id="mov-quantidade"
                        type="number"
                        step="any"
                        min={0}
                        className="input estab-field"
                        value={form.quantity}
                        onChange={(e) => handleChange('quantity', e.target.value)}
                        required
                    />
                </SettingsRow>
                {form.type === 'AJUSTE' && (
                    <SettingsRow stacked>
                        <div className="sf-segmented" role="group" aria-label="Direção do ajuste">
                            <button
                                type="button"
                                aria-pressed={form.adjustmentDirection === 'IN'}
                                onClick={() => handleChange('adjustmentDirection', 'IN')}
                            >
                                Adicionar ao saldo
                            </button>
                            <button
                                type="button"
                                aria-pressed={form.adjustmentDirection === 'OUT'}
                                onClick={() => handleChange('adjustmentDirection', 'OUT')}
                            >
                                Retirar do saldo
                            </button>
                        </div>
                    </SettingsRow>
                )}
                <SettingsRow label="Motivo" htmlFor="mov-motivo">
                    <input
                        id="mov-motivo"
                        className="input estab-field"
                        value={form.reasonCode}
                        onChange={(e) => handleChange('reasonCode', e.target.value)}
                        placeholder={form.type === 'AJUSTE' ? 'Recomendado' : 'Opcional'}
                    />
                </SettingsRow>
                <SettingsRow label="Custo unitário">
                    <PriceInput
                        label=""
                        className="estab-field w-full"
                        value={form.unitCost}
                        onChange={(e) => handleChange('unitCost', e.target.value)}
                        placeholder="Opcional"
                    />
                </SettingsRow>
                <SettingsRow label="Observação" htmlFor="mov-observacao" stacked>
                    <textarea
                        id="mov-observacao"
                        className="textarea estab-field !ml-0 !min-h-[4.5rem] !w-full"
                        value={form.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        placeholder="Detalhes adicionais"
                    />
                </SettingsRow>
            </SettingsSection>

            {warnings.length > 0 && (
                <div className="sf-group p-4 text-sm text-base-content/70" role="status">
                    <p className="font-semibold text-warning">Registrado com avisos</p>
                    <ul className="mt-1 list-disc pl-4">
                        {warnings.map((warning) => (
                            <li key={warning}>{MOVEMENT_WARNING_LABELS[warning] || warning}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="estab-savebar">
                <button
                    type="submit"
                    className="btn btn-primary w-full sm:w-auto sm:min-w-[11rem]"
                    disabled={saving || loadingItems}
                >
                    {saving && <span className="loading loading-spinner loading-sm"></span>}
                    Registrar
                </button>
            </div>
        </form>
    );
}
